import { toast as sonner } from 'sonner';

export interface NotifyOptions {
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

const base = (options?: NotifyOptions) => ({
  ...(options?.description ? { description: options.description } : {}),
  ...(options?.duration ? { duration: options.duration } : {}),
  ...(options?.action
    ? { action: { label: options.action.label, onClick: options.action.onClick } }
    : {}),
});

export interface UndoOptions extends Omit<NotifyOptions, 'action'> {
  onUndo: () => void;
  /** Runs when the window closes without an undo — the point of no return. */
  onCommit?: () => void;
  label?: string;
}

/**
 * Destructive-but-reversible actions apply immediately and offer a window to
 * take it back, instead of asking first.
 *
 * `onCommit` is where the irreversible half belongs (the real DELETE), so the
 * UI stays instant and the server only hears about it once nobody undid it.
 */
function undo(message: string, { onUndo, onCommit, label = 'Deshacer', ...rest }: UndoOptions) {
  const duration = rest.duration ?? 6000;
  let undone = false;

  const id = sonner(message, {
    ...base({ ...rest, duration }),
    action: {
      label,
      onClick: () => {
        undone = true;
        onUndo();
        sonner.dismiss(id);
      },
    },
    onAutoClose: () => {
      if (!undone) onCommit?.();
    },
    onDismiss: () => {
      if (!undone) onCommit?.();
    },
  });

  return id;
}

/** Failures always offer a way out: a retry, or at least the reason. */
function failure(message: string, options?: NotifyOptions & { onRetry?: () => void }) {
  return sonner.error(message, {
    ...base(options),
    ...(options?.onRetry ? { action: { label: 'Reintentar', onClick: options.onRetry } } : {}),
  });
}

export const notify = {
  success: (message: string, options?: NotifyOptions) => sonner.success(message, base(options)),
  info: (message: string, options?: NotifyOptions) => sonner.info(message, base(options)),
  warning: (message: string, options?: NotifyOptions) => sonner.warning(message, base(options)),
  error: failure,
  loading: (message: string, options?: NotifyOptions) => sonner.loading(message, base(options)),
  message: (message: string, options?: NotifyOptions) => sonner(message, base(options)),
  dismiss: (id?: string | number) => sonner.dismiss(id),
  undo,
  /** Ties a toast to a request: loading, then the success or the error message. */
  promise: <T>(
    promise: Promise<T>,
    messages: { loading: string; success: string | ((value: T) => string); error: string },
  ) => sonner.promise(promise, messages),
};
